import { useCallback, useEffect, useState, type FC } from "react";
import { Section, WizardNew as Wizard } from "components/wizard";
import Configure from "./Configure";
import { useWizardStepsManager } from "hooks/useWizardStepsManager";
import { PublishedStatusTypes, RouteNames } from "enums";
import { useLocation, useNavigate } from "react-router";
import navigationUtils from "utils/navigationUtils";
import SettingsTab from "../Settings/SettingsTab";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { connect, type ConnectedProps } from "react-redux";
import { publishExternalLink } from "../state/thunks/externalLinkCommandThunk";
import AddAssetsToPackModal from "components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";
import { useRtn } from "hooks/useRtn";
import { LinkPublishSuccess } from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { resetLinkDetails } from "../state/ExternalLinkDetailsSlice";
import { reset } from "../state/ExternalLinksOverviewSlice";
import { resetState as resetEntityState } from "../state/ExternalLinkEntityStateSlice";
import { Dimmer, Loader } from "semantic-ui-react";

interface CreateExternalLinkProps extends PropsFromRedux {}

const CreateLinkSteps = {
  Configuration: 0,
  Settings: 1,
};

const pageIndexes = [CreateLinkSteps.Configuration, CreateLinkSteps.Settings];

const CreateExternalLink: FC<CreateExternalLinkProps> = ({
  publishLink,
  id,
  resetOverview,
  resetEntityState,
  isSaving,
  dateModified,
  onUnmount,
}) => {
  const [wizardPages, { onNext, onPrevious, onFinish }, subscribeOnActiveIndexChanged] =
    useWizardStepsManager(pageIndexes);
  const [activeTab, setActiveTab] = useState(0);
  const [showAddPacks, setShowAddPacks] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onIsValidChange = useCallback((isValid: boolean) => setIsValid(isValid), []);
  const handleCancelClick = () => navigationUtils.goBackOrDefault(location, navigate, `/${RouteNames.contentLinks}`);

  useEffect(() => {
    const handler = (_: any, activeStepIndex: number) => {
      setActiveTab(activeStepIndex);
    };
    return subscribeOnActiveIndexChanged(handler);
  }, [subscribeOnActiveIndexChanged]);

  const handleOnFinish = () => {
    onFinish?.();
    publishLink(id);
  };

  useEffect(() => {
    return () => {
      resetOverview();
      onUnmount();
      resetEntityState();
    };
  }, [onUnmount, resetEntityState, resetOverview]);

  const onPublished = useCallback(() => setShowAddPacks(true), []);

  useRtn(LinkPublishSuccess, onPublished);

  const onPreviousAsync = async (activeStepIndex: number, nextIndex: number) => {
    onPrevious(activeStepIndex, nextIndex);
  };

  return (
    <>
      <Dimmer active={isSaving} inverted>
        <Loader active={isSaving} />
      </Dimmer>
      <Wizard
        title="Create Link"
        finishButtonLabel="Finish"
        onCancel={handleCancelClick}
        onProgress={onNext}
        onRegressAsync={onPreviousAsync}
        onFinish={handleOnFinish}
        publishedStatus={PublishedStatusTypes.notPublished}
        isSaveInProgress={isSaving}
        progressSavedDate={activeTab === CreateLinkSteps.Configuration ? dateModified : undefined}
      >
        <Section label="Configuration" className="scrollable-content" required isLocked={!isValid}>
          <Configure acceptHandlers={wizardPages[CreateLinkSteps.Configuration]} onIsValidChange={onIsValidChange} />
        </Section>
        <Section label="Settings" className="scrollable-content">
          <SettingsTab acceptHandlers={wizardPages[CreateLinkSteps.Settings]} />
        </Section>
      </Wizard>
      <AddAssetsToPackModal
        showModal={showAddPacks}
        onClose={/* istanbul ignore next */ () => navigate(`/${RouteNames.contentLinks}`)}
        onComplete={() => {}}
        selectedItemIds={[id]}
        contentType="Link"
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const base = state.library.externalLinks;
  return {
    id: base.entityState.entityId,
    isSaving: base.entityState.changingEntityState,
    dateModified: base.entityState.lastModifiedDateTime,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  publishLink: bindAction(publishExternalLink, dispatch),
  onUnmount: bindAction(resetLinkDetails, dispatch),
  resetOverview: bindAction(reset, dispatch),
  resetEntityState: bindAction(resetEntityState, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(CreateExternalLink);

export default ConnectedComponent;
